import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import InstallBanner from "@/components/InstallBanner";
import WelcomeModal from "@/components/WelcomeModal";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import {
  Flame, Calendar, Heart, Trophy, ChevronRight, Gift, HeartPulse,
  Activity as ActivityIcon, Plus, Timer,
} from "lucide-react";

// Refresh whenever any of these app events fire — components dispatch them after sync/log/etc.
const REFRESH_EVENTS = ["activity:synced", "session:logged", "recovery:logged", "manual:logged"];

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [d, setD] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);
  const [showManualLog, setShowManualLog] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const loadAll = () => {
    // Fetch dashboard + training/full in parallel — we use /training/full as the
    // authoritative source for today's session (same source as the Training page).
    Promise.all([
      api.get("/dashboard"),
      api.get("/training/full").catch(() => ({ data: null })),
    ]).then(([dash, full]) => {
      const fullData = full?.data;
      let todayFromFull = null;
      let weeklyKmFromFull = null;
      if (fullData?.program && Array.isArray(fullData.weeks) && fullData.weeks.length) {
        const todayIso = new Date().toISOString().slice(0, 10);
        const currentWeek = fullData.weeks.find(w => w.starts_on <= todayIso && todayIso <= w.ends_on)
          || fullData.weeks[fullData.current_week_index || 0]
          || fullData.weeks[fullData.weeks.length - 1];
        if (currentWeek) {
          todayFromFull = currentWeek.sessions.find(s => s.is_today) || null;
          // Weekly km = sum of planned distance for COMPLETED sessions in current week
          // BUT only sessions whose date is today or earlier — don't pre-credit km from
          // sessions the athlete marked complete in advance.
          const todayIsoLocal = new Date().toISOString().slice(0, 10);
          weeklyKmFromFull = currentWeek.sessions
            .filter(s => s.completed && (s.date || "") <= todayIsoLocal)
            .reduce((sum, s) => sum + (s.distance_km || 0), 0);
        }
      }
      // Merge: backend dashboard payload + frontend-resolved overrides for today's session & weekly km.
      // We override only when /training/full produced a non-null result, so we never *lose* data.
      const merged = { ...dash.data };
      if (todayFromFull) merged.today_session = todayFromFull;
      if (fullData?.program) merged.has_program = true;
      // If backend weekly_mileage is 0 but we computed something from completed planned sessions, prefer that.
      if (
        weeklyKmFromFull != null &&
        weeklyKmFromFull > 0 &&
        (!merged.weekly_mileage || merged.weekly_mileage === 0)
      ) {
        merged.weekly_mileage = weeklyKmFromFull;
      }
      setD(merged);
    }).catch(() => {});
    api.get("/broadcasts").then(r => setBroadcasts(r.data.slice(0, 2))).catch(() => {});
  };

  useEffect(() => {
    loadAll();
    const onFocus = () => loadAll();
    const onEvt = () => loadAll();
    window.addEventListener("focus", onFocus);
    REFRESH_EVENTS.forEach(e => window.addEventListener(e, onEvt));
    return () => {
      window.removeEventListener("focus", onFocus);
      REFRESH_EVENTS.forEach(e => window.removeEventListener(e, onEvt));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-login welcome modal
  useEffect(() => {
    if (user && user.role !== "admin" && user.welcomed === false) {
      const t = setTimeout(() => setShowWelcome(true), 500);
      return () => clearTimeout(t);
    }
  }, [user]);

  const weeklyGoal = d?.weekly_goal_km || 30;
  const weeklyKm = d?.weekly_mileage || 0;
  const weeklyPct = Math.min(100, Math.round((weeklyKm / weeklyGoal) * 100));

  // Next race countdown ring values — derived from user.target_race_date set in onboarding
  const nextRaceDays = d?.next_race_days;
  let nextRaceValue, nextRaceUnit, nextRaceSubLabel, nextRacePct;
  if (nextRaceDays == null) {
    nextRaceValue = "—";
    nextRaceUnit = "";
    nextRaceSubLabel = "Set race date";
    nextRacePct = 0;
  } else if (nextRaceDays < 0) {
    nextRaceValue = "✓";
    nextRaceUnit = "";
    nextRaceSubLabel = "Race day passed";
    nextRacePct = 100;
  } else if (nextRaceDays === 0) {
    nextRaceValue = "GO";
    nextRaceUnit = "";
    nextRaceSubLabel = "Race day";
    nextRacePct = 100;
  } else {
    nextRaceValue = nextRaceDays;
    nextRaceUnit = nextRaceDays === 1 ? "day" : "days";
    nextRaceSubLabel = "to race day";
    // ring fills as race approaches — full at race, empty 180+ days out
    nextRacePct = Math.max(0, Math.min(100, ((180 - nextRaceDays) / 180) * 100));
  }

  // Recovery readiness color
  const rec = d?.recovery_band;
  const recColor = rec?.color || "#666";
  const recScore = d?.recovery_score;

  return (
    <AppShell>
      <InstallBanner />
      <div data-testid="dashboard-page">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">{new Date().toDateString().toUpperCase()}</div>
            <h1 className="font-display text-4xl sm:text-5xl uppercase">Hello, <span className="text-[#d7ff00]">{user?.name?.split(" ")[0] || "Runner"}</span></h1>
          </div>
        </div>

        {/* Today's session hero */}
        <div className="mt-6 bg-[#0a0a0a] border border-[#1f1f1f] p-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#d7ff00]/5 to-transparent pointer-events-none" />
          <div className="relative">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">TODAY'S SESSION</div>
            {d?.today_session ? (
              <>
                <div className="mt-2 font-display text-3xl sm:text-4xl uppercase">{d.today_session.name}</div>
                <p className="mt-2 text-[#bbb] text-sm">{d.today_session.description}</p>
                <div className="mt-4 flex flex-wrap gap-2 font-mono-jp text-[10px] uppercase tracking-widest">
                  {d.today_session.distance_km && <span className="border border-[#222] px-2 py-1 text-[#bbb]">{d.today_session.distance_km} KM</span>}
                  {d.today_session.pace_target && <span className="border border-[#222] px-2 py-1 text-[#bbb]">{d.today_session.pace_target}</span>}
                  {d.today_session.hr_zone && <span className="border border-[#222] px-2 py-1 text-[#d7ff00]">{d.today_session.hr_zone}</span>}
                </div>
                <Link to="/app/training" className="mt-5 inline-flex items-center gap-2 bg-[#d7ff00] text-black font-display tracking-wider uppercase px-5 py-2.5 hover:bg-white transition-colors" data-testid="open-training">Open Training Plan <ChevronRight className="w-4 h-4" /></Link>
              </>
            ) : d?.has_program ? (
              <div className="mt-2 text-[#888] text-sm">Rest day. Easy mobility recommended.</div>
            ) : (
              <div className="mt-3" data-testid="dashboard-new-athlete">
                <div className="font-display text-2xl sm:text-3xl uppercase leading-tight">Coach JP is preparing<br />your <span className="text-[#d7ff00]">program</span></div>
                <p className="mt-3 text-[#bbb] text-sm">It will be ready within 24 hours. If this message persists, Coach JP has been notified automatically.</p>
              </div>
            )}
          </div>
        </div>

        {/* Primary metric rings — premium circular progress (6 rings = balanced 2×3 mobile, 3×2 tablet, 6×1 desktop) */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <RingMetric
            label="WEEKLY KM"
            sublabel={`of ${weeklyGoal} km goal`}
            value={weeklyKm}
            unit="km"
            pct={weeklyPct}
            color="#d7ff00"
            icon={ActivityIcon}
            testid="ring-weekly-km"
          />
          <RingMetric
            label="MONTHLY KM"
            sublabel="this month"
            value={(d?.monthly_mileage ?? 0).toFixed(0)}
            unit="km"
            pct={Math.min(100, ((d?.monthly_mileage || 0) / Math.max(1, weeklyGoal * 4)) * 100)}
            color="#71c4ff"
            icon={Calendar}
            testid="ring-monthly-km"
          />
          <RingMetric
            label="YEARLY KM"
            sublabel={new Date().getFullYear().toString()}
            value={(d?.yearly_mileage ?? 0).toFixed(0)}
            unit="km"
            pct={Math.min(100, ((d?.yearly_mileage || 0) / Math.max(1, weeklyGoal * 52)) * 100)}
            color="#c084fc"
            icon={Trophy}
            testid="ring-yearly-km"
          />
          <RingMetric
            label="RECOVERY"
            sublabel={rec?.band || "Not logged"}
            value={recScore != null ? recScore : "—"}
            unit={recScore != null ? "/100" : ""}
            pct={recScore != null ? recScore : 0}
            color={recColor}
            icon={Heart}
            link={{ to: "/app/recovery", text: rec ? null : "Log →" }}
            testid="ring-recovery"
          />
          <RingMetric
            label="CONSISTENCY"
            sublabel={d ? `${d.consistency_completed || 0} / ${d.consistency_planned || 0} done` : "—"}
            value={d?.consistency_pct ?? 0}
            unit="%"
            pct={d?.consistency_pct ?? 0}
            color={consistencyColor(d?.consistency_pct)}
            icon={Flame}
            testid="ring-consistency"
          />
          <RingMetric
            label="NEXT RACE"
            sublabel={nextRaceSubLabel}
            value={nextRaceValue}
            unit={nextRaceUnit}
            pct={nextRacePct}
            color="#ff9500"
            icon={Timer}
            link={!d?.next_race_date ? { to: "/onboarding", text: "Set →" } : undefined}
            testid="ring-next-race"
          />
        </div>

        {/* Quick log buttons */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <button
            onClick={() => setShowManualLog(true)}
            className="bg-[#0a0a0a] border border-[#d7ff00]/40 hover:border-[#d7ff00] hover:bg-[#d7ff00]/5 transition-colors p-3 flex items-center justify-center gap-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]"
            data-testid="log-manual-workout-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Log a workout
          </button>
          <Link
            to="/app/recovery"
            className="bg-[#0a0a0a] border border-[#1f1f1f] hover:border-[#d7ff00] hover:bg-[#d7ff00]/5 transition-colors p-3 flex items-center justify-center gap-2 font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] hover:text-[#d7ff00]"
            data-testid="quick-recovery-link"
          >
            <HeartPulse className="w-3.5 h-3.5" /> Log recovery
          </Link>
        </div>

        {/* Readiness banner / prompt */}
        {d && (
          rec ? (
            <div
              className="mt-4 bg-[#0a0a0a] border-l-4 p-4 flex items-center justify-between flex-wrap gap-3 transition-shadow"
              style={{ borderLeftColor: recColor, boxShadow: `inset 0 0 60px ${recColor}0c` }}
              data-testid="readiness-banner"
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${recColor}1a`, border: `1px solid ${recColor}66` }}
                >
                  <HeartPulse className="w-5 h-5" style={{ color: recColor }} />
                </div>
                <div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest" style={{ color: recColor }}>{rec.band} · {recScore}/100</div>
                  <div className="text-[#ddd] text-sm">{rec.message}</div>
                </div>
              </div>
              <Link to="/app/recovery" className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] hover:text-white" data-testid="readiness-view-link">View details →</Link>
            </div>
          ) : (
            <Link to="/app/recovery" className="mt-4 bg-gradient-to-br from-[#d7ff00]/10 to-transparent border border-[#d7ff00]/40 p-4 flex items-center justify-between group hover:border-[#d7ff00] transition" data-testid="readiness-prompt">
              <div className="flex items-center gap-3">
                <HeartPulse className="w-5 h-5 text-[#d7ff00]" />
                <div>
                  <div className="font-display text-lg uppercase tracking-wider">Log today's recovery</div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">30 seconds — sleep, HRV, RHR, mood — get your readiness score</div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[#d7ff00] group-hover:translate-x-1 transition" />
            </Link>
          )
        )}

        {/* Coach message + Race countdown */}
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <div className="bg-[#0a0a0a] border border-[#1f1f1f] border-l-4 border-l-[#d7ff00] p-5">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">COACH JP'S NOTE</div>
            <p className="mt-2 text-[#ddd] text-sm leading-relaxed">{d?.coach_message}</p>
          </div>
          <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-5">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] flex items-center gap-2"><Calendar className="w-3 h-3 text-[#d7ff00]" /> NEXT RACE</div>
            <div className="mt-2 font-display text-4xl text-[#d7ff00] tick">{d?.next_race_date || "—"}</div>
            <div className="font-mono-jp text-xs text-[#888]">{user?.goal?.toUpperCase() || "RACE"} TARGET</div>
          </div>
        </div>

        {/* Broadcasts */}
        {broadcasts.length > 0 && (
          <div className="mt-6">
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">FROM COACH JP</div>
            <div className="mt-3 space-y-3">
              {broadcasts.map(b => (
                <div key={b.id} className="bg-[#0a0a0a] border border-[#1f1f1f] p-4 flex gap-3" data-testid={`broadcast-${b.id}`}>
                  <img src="/icon-192.png" alt="JP" className="w-8 h-8 rounded-full border border-[#d7ff00]/40 object-cover flex-shrink-0" />
                  <div>
                    <div className="font-display uppercase text-lg">{b.title}</div>
                    <div className="text-xs text-[#bbb]">{b.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referral CTA */}
        <Link to="/app/referrals" className="mt-6 bg-gradient-to-br from-[#d7ff00]/10 to-transparent border border-[#d7ff00]/40 p-5 flex items-center justify-between hover:border-[#d7ff00] transition group" data-testid="dashboard-referral-cta">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-[#d7ff00]/15 border border-[#d7ff00]/40 flex items-center justify-center shrink-0">
              <Gift className="w-5 h-5 text-[#d7ff00]" />
            </div>
            <div>
              <div className="font-display text-lg uppercase tracking-wider">Give a week. Get a week.</div>
              <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Invite a friend — you both earn a free week</div>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-[#d7ff00] group-hover:translate-x-1 transition" />
        </Link>

        {showManualLog && (
          <ManualLogModal
            onClose={() => setShowManualLog(false)}
            onSaved={() => { setShowManualLog(false); loadAll(); }}
          />
        )}

        {showWelcome && (
          <WelcomeModal
            user={user}
            onClose={() => { setShowWelcome(false); refresh?.(); }}
          />
        )}
      </div>
    </AppShell>
  );
}

// ===== Components =====

function ManualLogModal({ onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "",
    type: "easy",
    distance_km: "",
    duration_min: "",
    pace: "",
    intervals: "",
    notes: "",
    feel: "good",
    rpe: 6,
    workout_date: new Date().toISOString().slice(0, 10),
  });
  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Workout name required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/workouts/manual", {
        ...form,
        distance_km: form.distance_km ? Number(form.distance_km) : null,
        duration_min: form.duration_min ? Number(form.duration_min) : null,
        rpe: form.rpe ? Number(form.rpe) : null,
      });
      window.dispatchEvent(new CustomEvent("manual:logged"));
      toast.success("Workout logged");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={() => !busy && onClose()} data-testid="manual-log-modal">
      <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-lg my-6 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1.5"><Plus className="w-3 h-3" /> LOG WORKOUT</div>
            <h3 className="font-display text-2xl uppercase mt-1">Ad-hoc activity</h3>
            <p className="text-[10px] font-mono-jp text-[#666] uppercase tracking-widest mt-1">Not on your plan? Log it here. Counts toward weekly / monthly stats.</p>
          </div>
          {!busy && <button onClick={onClose} className="text-[#888]" data-testid="manual-close">✕</button>}
        </div>

        <Field label="Workout name *">
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Easy Park Run, Lake Gardens Long Run" className={fldCls} data-testid="manual-name" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className={fldCls} data-testid="manual-type">
              {["easy","long","threshold","tempo","vo2","hill","recovery","race","cross","strength","mobility"].map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
            </select>
          </Field>
          <Field label="Date">
            <input type="date" value={form.workout_date} onChange={e => setForm({ ...form, workout_date: e.target.value })} className={fldCls} data-testid="manual-date" />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <Field label="Distance (km)">
            <input type="number" step="0.1" value={form.distance_km} onChange={e => setForm({ ...form, distance_km: e.target.value })} placeholder="8.5" className={fldCls} data-testid="manual-distance" />
          </Field>
          <Field label="Duration (min)">
            <input type="number" value={form.duration_min} onChange={e => setForm({ ...form, duration_min: e.target.value })} placeholder="45" className={fldCls} data-testid="manual-duration" />
          </Field>
          <Field label="Pace">
            <input value={form.pace} onChange={e => setForm({ ...form, pace: e.target.value })} placeholder="5:30" className={fldCls} data-testid="manual-pace" />
          </Field>
        </div>

        <Field label="Intervals / splits (optional)">
          <textarea rows={2} value={form.intervals} onChange={e => setForm({ ...form, intervals: e.target.value })} placeholder="e.g. 400m × 6: 1:25, 1:24, 1:26…" className={fldCls} data-testid="manual-intervals" />
        </Field>

        <Field label="Notes for Coach JP">
          <textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="How did it feel?" className={fldCls} data-testid="manual-notes" />
        </Field>

        <Field label="How was it?">
          <div className="flex flex-wrap gap-1">
            {["great","good","average","tired","very_tired","pain"].map(f => (
              <button key={f} onClick={() => setForm({ ...form, feel: f })} className={`px-2 py-1.5 font-mono-jp text-[10px] uppercase tracking-widest border ${form.feel === f ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10" : "border-[#222] text-[#bbb]"}`} data-testid={`manual-feel-${f}`}>{f.replace("_", " ")}</button>
            ))}
          </div>
        </Field>

        <Field label={`RPE · ${form.rpe}/10`}>
          <input type="range" min={1} max={10} value={form.rpe} onChange={e => setForm({ ...form, rpe: e.target.value })} className="w-full accent-[#d7ff00]" data-testid="manual-rpe" />
        </Field>

        <button onClick={submit} disabled={busy || !form.name.trim()} className="mt-4 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="manual-save">
          <Plus className="w-4 h-4" /> {busy ? "Saving…" : "Log workout"}
        </button>
      </div>
    </div>
  );
}

const fldCls = "w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2 text-sm text-white";

function Field({ label, children }) {
  return (
    <div className="mt-3">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function RingMetric({ label, sublabel, value, unit, pct, color, icon: Icon, link, testid, topRight }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  const isEmpty = value === "—" || (typeof value === "number" && value === 0 && clamped === 0);
  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-4 sm:p-5 relative overflow-hidden" data-testid={testid}>
      <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: `radial-gradient(circle at 100% 0%, ${color}33, transparent 70%)` }} />
      {topRight && <div className="absolute top-2 right-2 z-10">{topRight}</div>}
      <div className="relative">
        <div className="flex items-center gap-1.5 font-mono-jp text-[9px] uppercase tracking-widest text-[#888]">
          {Icon && <Icon className="w-3 h-3" style={{ color }} />} {label}
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Ring pct={clamped} color={color} size={64} stroke={5}>
            <div className="flex flex-col items-center justify-center leading-none">
              <div className="font-display text-base" style={{ color: isEmpty ? "#555" : color }}>{value}</div>
              {unit && <div className="font-mono-jp text-[7px] text-[#666] mt-0.5">{unit}</div>}
            </div>
          </Ring>
          <div className="min-w-0 flex-1">
            <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#666] truncate">{sublabel}</div>
            {link && link.text && (
              <Link to={link.to} className="mt-1 inline-block font-mono-jp text-[10px] uppercase tracking-widest" style={{ color }} data-testid={`${testid}-link`}>
                {link.text}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Ring({ pct, color, size = 64, stroke = 5, children }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#1a1a1a" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={color} strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 800ms ease-out", filter: `drop-shadow(0 0 4px ${color}66)` }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">{children}</div>
    </div>
  );
}

function consistencyColor(pct) {
  if (pct == null) return "#666";
  if (pct >= 80) return "#d7ff00";
  if (pct >= 50) return "#ffb800";
  return "#ff6b35";
}
